import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import Seo from "../components/site/Seo";
import { Container } from "../components/site/Ui";
import { api } from "../lib/api";

const POLL_INTERVAL = 2000;
const MAX_POLLS = 15;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      if (polls.current >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const content = {
    checking: {
      icon: <Loader2 className="h-7 w-7 animate-spin text-slate-400" aria-hidden="true" />,
      title: "Confirming your payment…",
      body: "This usually takes a few seconds. Please keep this page open.",
    },
    paid: {
      icon: <CheckCircle2 className="h-7 w-7 text-emerald-600" aria-hidden="true" />,
      title: "Payment received. Your plan is active.",
      body: "Your subscription is now active and your invoice is available in Billing. Next step: make sure your Brand Profile is complete so we can start building your content.",
    },
    failed: {
      icon: <XCircle className="h-7 w-7 text-red-600" aria-hidden="true" />,
      title: "That payment didn't go through",
      body: "Nothing was charged. You can try again from your subscription page — iDEAL and card are both supported.",
    },
    timeout: {
      icon: <Loader2 className="h-7 w-7 text-slate-400" aria-hidden="true" />,
      title: "Still processing",
      body: "Your bank is taking a little longer than usual. Your subscription page will update automatically once the payment clears.",
    },
    error: {
      icon: <XCircle className="h-7 w-7 text-red-600" aria-hidden="true" />,
      title: "We couldn't check that payment",
      body: "Open your subscription page to see the current status of your plan.",
    },
  }[state];

  return (
    <>
      <Seo title="Payment status" description="Your PostMint payment status." path="/payment/success" />
      <Container className="flex min-h-[60vh] items-center justify-center py-20">
        <div
          data-testid={`payment-status-${state}`}
          className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-8 text-center soft-shadow"
        >
          <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-50">
            {content.icon}
          </span>
          <h1 className="mt-5 font-display text-2xl font-bold text-slate-900">{content.title}</h1>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-600">{content.body}</p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to="/dashboard/setup"
              data-testid="payment-success-profile-link"
              className="rounded-full bg-[#F95738] px-6 py-3.5 text-base font-semibold text-white"
            >
              Set up my brand
            </Link>
            <Link
              to="/dashboard/subscription"
              data-testid="payment-success-subscription-link"
              className="rounded-full border border-slate-300 px-6 py-3.5 text-base font-semibold text-slate-800"
            >
              View subscription
            </Link>
          </div>
        </div>
      </Container>
    </>
  );
}

export function PaymentCancel() {
  return (
    <>
      <Seo title="Payment cancelled" description="Your PostMint payment was cancelled." path="/payment/cancel" />
      <Container className="flex min-h-[60vh] items-center justify-center py-20">
        <div data-testid="payment-cancelled" className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-8 text-center soft-shadow">
          <h1 className="font-display text-2xl font-bold text-slate-900">Payment cancelled</h1>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
            No payment was taken and nothing changed on your account. You can pick up where you left off whenever you're
            ready.
          </p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <Link to="/dashboard/subscription" data-testid="payment-cancel-retry-link" className="rounded-full bg-[#F95738] px-6 py-3.5 text-base font-semibold text-white">
              Back to subscription
            </Link>
            <Link to="/pricing" data-testid="payment-cancel-pricing-link" className="rounded-full border border-slate-300 px-6 py-3.5 text-base font-semibold text-slate-800">
              Compare plans
            </Link>
          </div>
        </div>
      </Container>
    </>
  );
}
